import isNumber from 'lodash/isNumber';
import isNaN from 'lodash/isNaN';
import clamp from 'lodash/clamp';
import forEach from 'lodash/forEach';
import matches from 'lodash/matches';
import isObject from 'lodash/isObject';
import pick from 'lodash/pick';
import first from 'lodash/first';
import take from 'lodash/take';
import last from 'lodash/last';
import words from 'lodash/words';
import lastIndexOf from 'lodash/lastIndexOf';
import split from 'lodash/split';

const CURRENCY_FORMAT = { style: 'currency', currency: 'USD', minimumFractionDigits: 2 };

export const formatNumberToCurrency = (balance, options = {}) =>
  isNumber(balance) ? balance.toLocaleString('en-US', { ...CURRENCY_FORMAT, ...options }) : '';

export const formatDescriptionForPayoutMethod = (payoutSettings = {}) => {
  const { bankAccount, card } = pick(payoutSettings.stripeAccount, ['loginUrl', 'bankAccount', 'card']);

  let description = '';

  if (bankAccount) {
    const { bankName, last4, accountHolderName } = bankAccount;

    description = `${bankName} account ending in ${last4}`;

    if (accountHolderName) {
      description = description.concat(` belonging to ${accountHolderName}.`);
    }
  } else if (card) {
    description = `${card.brand} card ending in ${card.last4} expiring ${card.expMonth}/${card.expYear}`;
  }

  return description;
};

// Prefer using cloneDeep obj to create a deep copy of the original object to avoid mutating it.
export const findAndReplace = (obj, matchAttributes, replaceValue) => {
  const isMatch = matches(matchAttributes);

  forEach(obj, (value, key) => {
    if (isObject(value)) {
      if (isMatch(value)) {
        obj[key] = replaceValue;
        return;
      } else {
        findAndReplace(value, matchAttributes, replaceValue);
      }
    }
  });

  return obj;
};

export const getEncryptedValue = (value) => JSON.stringify(value);

export const formatFileSize = (bytes, options = {}) => {
  const { decimals = 2, base = 1024, locale = 'en-US' } = options;

  // Input validation
  if (!isNumber(bytes) || isNaN(bytes) || bytes < 0) {
    return 'Invalid input';
  }

  if (bytes === 0) return '0 Bytes';

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(base));

  // Using Lodash's clamp to ensure decimals is within a reasonable range
  const dm = clamp(decimals, 0, 20);

  const value = bytes / Math.pow(base, i);

  // Using Intl.NumberFormat for localized number formatting
  const formattedValue = new Intl.NumberFormat(locale, {
    minimumFractionDigits: dm,
    maximumFractionDigits: dm,
  }).format(value);

  return `${formattedValue} ${sizes[i]}`;
};

export const getAvatarString = (fullName) => {
  const nameParts = words(fullName);

  if (nameParts.length === 0) return null;
  if (nameParts.length === 1) {
    return take(nameParts[0], 1).join('');
  }

  const firstInitial = first(nameParts[0]);
  const lastInitial = first(last(nameParts));

  return firstInitial + lastInitial;
};

export const extractFileName = (url) => {
  const pathPart = last(split(url, '/'));
  const lastDotIndex = lastIndexOf(pathPart, '.');

  if (lastDotIndex <= 0) {
    return pathPart;
  }

  return pathPart.substring(0, lastDotIndex).replace(/^\d+_/, '');
};

export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : hex;
};
